import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { GIGGLE_ACADEMY } from "@/lib/config";
import Icon from "@/components/shared/Icon";
import Seo from "@/components/shared/Seo";
import PageHero from "@/components/shared/PageHero";

export default function About() {
  const { t, lp, code } = useLang();

  const roadmap = [
    { icon: "Rocket", en: "Phase 1 · Free learning hub, games, and stories (live)", zh: "第一阶段 · 免费学习中心、游戏与故事（已上线）" },
    { icon: "Globe", en: "Phase 2 · Expanded bilingual content and resources", zh: "第二阶段 · 扩充双语内容与资源" },
    { icon: "ShieldCheck", en: "Phase 3 · Transparent, verifiable charity infrastructure", zh: "第三阶段 · 透明、可验证的慈善基础设施" },
    { icon: "HeartHandshake", en: "Phase 4 · Confirmed on-chain contributions to education", zh: "第四阶段 · 向教育进行已确认的链上捐助" },
  ];

  return (
    <>
      <Seo title={t("about.title")} description={t("about.mission.desc")} />
      <PageHero eyebrow={t("nav.about")} title={t("about.title")} subtitle={t("about.mission.desc")} />

      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-3xl border-2 border-[#147DEB]/10 bg-white p-7">
            <Icon name="BookHeart" className="h-8 w-8 text-[#147DEB]" />
            <h2 className="font-display mt-4 text-2xl font-bold text-[#082B66]">{t("about.why.title")}</h2>
            <p className="mt-2 text-base leading-relaxed text-[#475569]">{t("about.why.desc")}</p>
          </div>
          <div className="rounded-3xl border-2 border-[#45C878]/20 bg-gradient-to-br from-[#EAFBF1] to-white p-7">
            <Icon name="Laptop" className="h-8 w-8 text-[#45C878]" />
            <h2 className="font-display mt-4 text-2xl font-bold text-[#082B66]">{t("about.tech.title")}</h2>
            <p className="mt-2 text-base leading-relaxed text-[#475569]">{t("about.tech.desc")}</p>
          </div>
        </div>

        {/* Philosophy */}
        <h2 className="font-display mt-14 text-2xl font-bold text-[#082B66]">{t("about.philosophy.title")}</h2>
        <div className="mt-5 grid gap-6 md:grid-cols-3">
          {[
            { k: "learn", icon: "GraduationCap", color: "#147DEB" },
            { k: "play", icon: "Gamepad2", color: "#FFC928" },
            { k: "give", icon: "HeartHandshake", color: "#45C878" },
          ].map((p) => (
            <div key={p.k} className="rounded-3xl border-2 border-[#147DEB]/10 bg-white p-6">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl" style={{ backgroundColor: `${p.color}1f` }}><Icon name={p.icon} className="h-6 w-6" style={{ color: p.color }} /></span>
              <h3 className="font-display mt-4 text-xl font-bold text-[#082B66]">{t(`home.philosophy.${p.k}`)}</h3>
              <p className="mt-2 text-sm text-[#475569]">{t(`home.philosophy.${p.k}Desc`)}</p>
            </div>
          ))}
        </div>

        {/* Community role */}
        <div className="mt-14 rounded-3xl border-2 border-[#147DEB]/10 bg-white p-7">
          <Icon name="Users" className="h-8 w-8 text-[#FF91B8]" />
          <h2 className="font-display mt-4 text-2xl font-bold text-[#082B66]">{t("about.community.title")}</h2>
          <p className="mt-2 text-base leading-relaxed text-[#475569]">{t("about.community.desc")}</p>
          <Link to={lp("/community")} className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#147DEB] hover:underline">{t("common.learnMore")} <Icon name="ArrowRight" className="h-4 w-4" /></Link>
        </div>

        {/* Roadmap */}
        <h2 className="font-display mt-14 text-2xl font-bold text-[#082B66]">{t("about.roadmap.title")}</h2>
        <div className="mt-5 space-y-4">
          {roadmap.map((r, i) => (
            <div key={i} className="flex items-start gap-4 rounded-3xl border-2 border-[#147DEB]/10 bg-white p-5">
              <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-[#E8F5FF]"><Icon name={r.icon} className="h-5 w-5 text-[#147DEB]" /></span>
              <p className="pt-2 text-base font-semibold text-[#082B66]">{code === "zh" ? r.zh : r.en}</p>
            </div>
          ))}
        </div>

        {/* Giggle Academy */}
        <div className="mt-14 rounded-3xl border-2 border-[#FFC928]/40 bg-gradient-to-br from-[#FFF9E6] to-white p-8" data-testid="about-giggle-section">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#FFC928] px-3 py-1 text-xs font-bold text-[#082B66]"><Icon name="Heart" className="h-4 w-4" /> Giggle Academy</span>
          <h2 className="font-display mt-4 text-2xl font-bold text-[#082B66]">{t("about.giggle.title")}</h2>
          <p className="mt-3 text-base leading-relaxed text-[#475569]">{t("about.giggle.desc")}</p>
          {GIGGLE_ACADEMY.officialUrl ? (
            <a href={GIGGLE_ACADEMY.officialUrl} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#147DEB] hover:underline">Giggle Academy <Icon name="ExternalLink" className="h-4 w-4" /></a>
          ) : (
            <p className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#94A3B8]"><Icon name="Link2Off" className="h-4 w-4" /> {t("common.comingSoon")}</p>
          )}
        </div>
      </section>
    </>
  );
}
